import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum OrderLinesFilterKeys {
  mall = "mall",
  orderDate = "orderDate",
  completedAt = "completedAt",
  shippingMethodName = "shippingMethodName",
  orderCourier = "orderCourier"
}

export enum deliveryType {
  EXPRESS = "express",
  EXPRESS_LONG_DISTANCE = "express_long_distance",
  IN_STORE_PICKUP = "in_store_pickup",
  NATIONWIDE_DELIVERY = "nationwide_delivery",
  STANDARD_DELIVERY = "next_day",
  SAME_DAY_DELIVERY = "same_day"
}

export enum orderCourier {
  GRAB = "GRAB",
  GRAB_EXPRESS = "GRAB EXPRESS",
  O2GO = "2GO",
  LALAMOVE = "LALAMOVE",
  IN_HOUSE = "IN HOUSE",
  OTHERS = "OTHERS",
  PANDAGO = "PANDAGO"
}

export interface OrderLinesListFilterOpts {
  orderDate: FilterOpts<MinMax>;
  completedAt: FilterOpts<MinMax>;
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  shippingMethodName: FilterOpts<string[]>;
  orderCourier: FilterOpts<string[]>;
}

const messages = defineMessages({
  completedAt: {
    defaultMessage: "Order Completion Date",
    description: "order completetion date"
  },

  express: {
    defaultMessage: "Express",
    description: "express"
  },

  expressLongDistance: {
    defaultMessage: "10km Express Delivery",
    description: "expressLongDistance"
  },

  grab: {
    defaultMessage: "Grab",
    description: "grab"
  },

  grabExpress: {
    defaultMessage: "Grab Express",
    description: "grab express"
  },

  inHouse: {
    defaultMessage: "In House",
    description: "in house"
  },

  inStorePickup: {
    defaultMessage: "In Store Pickup ",
    description: "in store pickup "
  },

  lalamove: {
    defaultMessage: "Lalamove",
    description: "lalamove"
  },

  mall: {
    defaultMessage: "Mall",
    description: "mall name"
  },

  nationWide: {
    defaultMessage: "Nationwide Delivery",
    description: "nation wide delivery"
  },

  o2GO: {
    defaultMessage: "2GO",
    description: "2go"
  },

  orderCourier: {
    defaultMessage: "Courier",
    description: "courier"
  },

  orderDate: {
    defaultMessage: "Order Created Date",
    description: "order created date"
  },

  others: {
    defaultMessage: "Others",
    description: "others"
  },

  pandaGo: {
    defaultMessage: "PandaGo",
    description: "pandago"
  },

  sameDayDelivery: {
    defaultMessage: "Same Day Delivery",
    description: "same day delivery"
  },

  shippingMethodName: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  },

  standardDelivery: {
    defaultMessage: "Standard Delivery",
    description: "standard delivery"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: OrderLinesListFilterOpts
): IFilter<OrderLinesFilterKeys> {
  return [
    {
      ...createDateField(
        OrderLinesFilterKeys.orderDate,
        intl.formatMessage(messages.orderDate),
        opts.orderDate.value
      ),
      active: opts.orderDate.active
    },
    {
      ...createDateField(
        OrderLinesFilterKeys.completedAt,
        intl.formatMessage(messages.completedAt),
        opts.completedAt.value
      ),
      active: opts.completedAt.active
    },
    {
      ...createAutocompleteField(
        OrderLinesFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    },
    {
      ...createOptionsField(
        OrderLinesFilterKeys.shippingMethodName,
        intl.formatMessage(messages.shippingMethodName),
        opts.shippingMethodName.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: deliveryType.EXPRESS
          },
          {
            label: intl.formatMessage(messages.standardDelivery),
            value: deliveryType.STANDARD_DELIVERY
          },
          {
            label: intl.formatMessage(messages.inStorePickup),
            value: deliveryType.IN_STORE_PICKUP
          },
          {
            label: intl.formatMessage(messages.expressLongDistance),
            value: deliveryType.EXPRESS_LONG_DISTANCE
          },
          {
            label: intl.formatMessage(messages.sameDayDelivery),
            value: deliveryType.SAME_DAY_DELIVERY
          },
          {
            label: intl.formatMessage(messages.nationWide),
            value: deliveryType.NATIONWIDE_DELIVERY
          }
        ]
      ),
      active: opts.shippingMethodName.active
    },

    {
      ...createOptionsField(
        OrderLinesFilterKeys.orderCourier,
        intl.formatMessage(messages.orderCourier),
        opts.orderCourier.value,
        true,
        [
          {
            label: intl.formatMessage(messages.grab),
            value: orderCourier.GRAB
          },
          {
            label: intl.formatMessage(messages.grabExpress),
            value: orderCourier.GRAB_EXPRESS
          },
          {
            label: intl.formatMessage(messages.o2GO),
            value: orderCourier.O2GO
          },
          {
            label: intl.formatMessage(messages.lalamove),
            value: orderCourier.LALAMOVE
          },
          {
            label: intl.formatMessage(messages.inHouse),
            value: orderCourier.IN_HOUSE
          },
          {
            label: intl.formatMessage(messages.others),
            value: orderCourier.OTHERS
          },
          {
            label: intl.formatMessage(messages.pandaGo),
            value: orderCourier.PANDAGO
          }
        ]
      ),
      active: opts.orderCourier.active
    }
  ];
}
